import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Container, Tab, Tabs, Typography } from '@mui/material';
import Header from '../../../Components/Header/Header';
import PublicUrlPanel from './PublicUrlPanel';
import AcceptanceSettingTab from './AcceptanceSettingTab';
import SeatTypesTab from './SeatTypesTab';
import BusinessHoursTab from './BusinessHoursTab';
import SpecialDaysTab from './SpecialDaysTab';
import CoursesTab from './CoursesTab';
import InquiriesTab from './InquiriesTab';
import PaymentPolicyTab from './PaymentPolicyTab';
import NotificationTab from './NotificationTab';
import { UNSAVED_CONFIRM_MESSAGE } from './SettingsShared';

/**
 * 予約設定ハブ画面。/:store_id/manage/reservation-settings（MANAGER のみ、ルート側で保護済み）。
 * 予約関連の店舗設定（受付設定/席・在庫/営業時間/特定日/コース/質問/決済方針/店舗情報〔通知・店舗写真含む〕）を
 * タブで切り替えて編集する。各タブは表示時に必要な fetch を自前で dispatch する。
 * フォーム型のタブは dirty（サーバ値と差分あり）を onDirtyChange で通知し、
 * dirty のままタブ切替・離脱しようとしたときに確認する（フィードバック3・保存し忘れ防止）。
 */
const ReservationSettings: React.FC = () => {
  const { store_id } = useParams<{ store_id: string }>();
  const [tab, setTab] = useState(0);
  // アクティブタブの未保存変更有無（フォーム型タブのみ通知してくる）
  const [dirty, setDirty] = useState(false);

  const handleDirtyChange = useCallback((next: boolean) => setDirty(next), []);

  // dirty のままブラウザバック・タブクローズしようとしたときのネイティブ確認
  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  if (!store_id) return null;

  const handleTabChange = (_: unknown, value: number) => {
    // eslint-disable-next-line no-alert
    if (dirty && !window.confirm(UNSAVED_CONFIRM_MESSAGE)) return;
    setDirty(false);
    setTab(value);
  };

  return (
    <>
      <Header />
      <Container component="main" maxWidth="md">
        <Box sx={{ mt: 3, mb: 6 }}>
          <Typography variant="h5" gutterBottom>
            予約設定
          </Typography>
          {/* 公開URL（Google Maps等への掲載用）はタブ外の常時表示。 */}
          <PublicUrlPanel storeId={store_id} />
          <Tabs
            value={tab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons
            allowScrollButtonsMobile
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
          >
            <Tab label="受付設定" />
            <Tab label="席・在庫" />
            <Tab label="営業時間" />
            <Tab label="特定日" />
            <Tab label="コース" />
            <Tab label="質問" />
            <Tab label="決済方針" />
            <Tab label="店舗情報" />
          </Tabs>
          {/* アクティブなタブのみマウントし、タブ表示のたびに各タブの useEffect で再フェッチさせる。
              席・在庫/特定日/コース/質問はダイアログ内で即時保存する CRUD のためタブ自体は dirty にならない */}
          {tab === 0 && <AcceptanceSettingTab storeId={store_id} onDirtyChange={handleDirtyChange} />}
          {tab === 1 && <SeatTypesTab storeId={store_id} />}
          {tab === 2 && <BusinessHoursTab storeId={store_id} onDirtyChange={handleDirtyChange} />}
          {tab === 3 && <SpecialDaysTab storeId={store_id} />}
          {tab === 4 && <CoursesTab storeId={store_id} />}
          {tab === 5 && <InquiriesTab storeId={store_id} />}
          {tab === 6 && <PaymentPolicyTab storeId={store_id} onDirtyChange={handleDirtyChange} />}
          {tab === 7 && <NotificationTab storeId={store_id} onDirtyChange={handleDirtyChange} />}
        </Box>
      </Container>
    </>
  );
};

export default ReservationSettings;
